import { useQuery } from "@tanstack/react-query";
import { querySeasonalityPublic, type EdgeResponse } from "../lib/seasonalityApi";
import type {
  IstatPresenzeMensili,
  IstatRicettivo,
  SeasonalityIndicator,
} from "../types/database";

// ============================================
// ISTAT PRESENZE MENSILI
// ============================================

export function useIstatPresenze(
  territorio?: string,
  anno?: number,
  residenza?: string,
  tipologia?: string,
) {
  return useQuery({
    queryKey: ["istat-presenze", territorio, anno, residenza, tipologia],
    queryFn: async () => {
      const result = await querySeasonalityPublic<EdgeResponse<IstatPresenzeMensili[]>>(
        "istat-presenze",
        { territorio, year: anno, residenza, tipologia },
      );
      return result.data || [];
    },
    enabled: !!territorio && !!anno,
  });
}

// ============================================
// ISTAT SEASONALITY INDICATORS (computed view)
// ============================================

export function useIstatIndicators(
  territorio?: string,
  anno?: number,
  residenza?: string,
  tipologia?: string,
) {
  return useQuery({
    queryKey: ["istat-indicators", territorio, anno, residenza, tipologia],
    queryFn: async () => {
      const result = await querySeasonalityPublic<EdgeResponse<SeasonalityIndicator[]>>(
        "istat-indicators",
        { territorio, year: anno, residenza, tipologia },
      );
      return result.data || [];
    },
    enabled: !!anno,
  });
}

// All indicators for a given year (for classification)
export function useAllIstatIndicators(anno?: number, residenza?: string, tipologia?: string) {
  return useQuery({
    queryKey: ["istat-indicators-all", anno, residenza, tipologia],
    queryFn: async () => {
      const result = await querySeasonalityPublic<EdgeResponse<SeasonalityIndicator[]>>(
        "istat-indicators",
        { year: anno, residenza, tipologia },
      );
      return result.data || [];
    },
    enabled: !!anno,
  });
}

// ============================================
// ISTAT RICETTIVO
// ============================================

export function useIstatRicettivo(
  provincia?: string,
  anno?: number,
  regione?: string,
) {
  return useQuery({
    queryKey: ["istat-ricettivo", provincia, anno, regione],
    queryFn: async () => {
      const result = await querySeasonalityPublic<EdgeResponse<IstatRicettivo[]>>(
        "istat-ricettivo",
        { provincia, year: anno, region: regione },
      );
      return result.data || [];
    },
  });
}

// ============================================
// CAPACITY INDICATORS (province level)
// ============================================

interface CapacityIndicator {
  provincia: string;
  anno: number;
  pl_prov: number;
  cpla_prov: number;
}

export function useIstatCapacity(provincia?: string, anno?: number) {
  return useQuery({
    queryKey: ["istat-capacity", provincia, anno],
    queryFn: async () => {
      const result = await querySeasonalityPublic<EdgeResponse<CapacityIndicator[]>>(
        "istat-capacity",
        { provincia, year: anno },
      );
      return result.data || [];
    },
  });
}

// ============================================
// AVAILABLE YEARS
// ============================================

interface AvailableYears {
  presenze: number[];
  ricettivo: number[];
  arrivi: number[];
}

export function useAvailableYears() {
  return useQuery({
    queryKey: ["available-years"],
    queryFn: async () => {
      const result = await querySeasonalityPublic<EdgeResponse<AvailableYears>>(
        "available-years",
        {},
      );
      return result.data;
    },
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });
}
